import type { SelectReservation } from '@workspace/db/extra'
import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  type BatchResult,
  editedReservationInput,
  isReservationConflict,
  type ReservationInput,
  useAddReservation,
  useAddReservationsBatch,
  useDeleteReservation,
  useEditReservationsBatch,
  useRefreshReservations,
  useReservations,
} from '../../../features/locations/use-reservations'
import { useIdOrCode } from '../../use-id-or-code'
import { Button, ConfirmDialog, Dialog, EmptyRow, Field, Inline, inputCls, SubCrumb, td, th } from './controls'
import { Badge, Card, mono, Notice, PageHeader, SkeletonCards, type Tone } from './ui'

const TYPES = ['workstation', 'printer', 'other']
const MAC_RE = /^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i
const statusTone = (s: string | null): Tone => (s === 'ACTIVE' ? 'ok' : s === 'CREATING' ? 'warn' : 'off')

function TypeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <>
      <input className={inputCls} list="v1-res-types" value={value} onChange={(e) => onChange(e.target.value)} />
      <datalist id="v1-res-types">
        {TYPES.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </>
  )
}

const FORCE_BODY =
  'Another reservation already uses this MAC or IP address. Forcing it will overwrite the existing reservation.'

function AddDialog({ idOrCode, code, onClose }: { idOrCode: string; code: string; onClose: () => void }) {
  const add = useAddReservation()
  const [mac, setMac] = useState('')
  const [type, setType] = useState('workstation')
  const [vlan, setVlan] = useState('100')
  const [conflict, setConflict] = useState(false)
  const valid = MAC_RE.test(mac.trim()) && type.trim() !== '' && vlan.trim() !== ''

  const submit = (force: boolean) =>
    add.mutate(
      { idOrCode, reservation: { code, mac: mac.trim().toLowerCase(), type: type.trim(), vlan, force } },
      { onSuccess: onClose, onError: (e) => setConflict(isReservationConflict(e)) },
    )

  return (
    <>
      <Dialog
        open={!conflict}
        title="Add a reservation"
        onClose={onClose}
        footer={
          <>
            <Button variant="quiet" onClick={onClose} disabled={add.isPending}>
              Cancel
            </Button>
            <Button onClick={() => submit(false)} disabled={!valid || add.isPending}>
              {add.isPending ? 'Adding...' : 'Add reservation'}
            </Button>
          </>
        }
      >
        <Field label="MAC address" hint="aa:bb:cc:dd:ee:ff">
          <input className={inputCls} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
        </Field>
        <Field label="Type">
          <TypeInput value={type} onChange={setType} />
        </Field>
        <Field label="VLAN">
          <input className={inputCls} inputMode="numeric" value={vlan} onChange={(e) => setVlan(e.target.value)} />
        </Field>
        {add.error && !conflict ? <Inline tone="bad">{apiErrorMessage(add.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={conflict}
        title="This reservation conflicts"
        body={FORCE_BODY}
        confirmLabel="Force add"
        danger
        pending={add.isPending}
        onConfirm={() => submit(true)}
        onClose={() => setConflict(false)}
      />
    </>
  )
}

/** Runs a batch; on a 409 stops and offers to force the conflicting item then continue. */
function useBatchRunner(
  run: (items: number[], force: boolean) => Promise<BatchResult>,
  total: number,
  onDone: () => void,
) {
  const [conflictAt, setConflictAt] = useState<number | null>(null)
  const [done, setDone] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const go = async (from: number, force: boolean) => {
    setPending(true)
    setError(null)
    try {
      const idx = Array.from({ length: total - from }, (_, i) => from + i)
      const res = await run(idx, force)
      setDone(from + res.processedCount)
      if (res.conflict) setConflictAt(from + res.conflict.index)
      else {
        setConflictAt(null)
        onDone()
      }
    } catch (e) {
      setError(apiErrorMessage(e))
    } finally {
      setPending(false)
    }
  }
  return { conflictAt, done, error, pending, go, clear: () => setConflictAt(null) }
}

function BatchAddDialog({ idOrCode, code, onClose }: { idOrCode: string; code: string; onClose: () => void }) {
  const batch = useAddReservationsBatch()
  const [text, setText] = useState('')
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
  const parsed: ReservationInput[] = lines.map((l) => {
    const [mac = '', type = 'workstation', vlan = '100'] = l.split(/[,\s]+/)
    return { code, mac: mac.toLowerCase(), type, vlan }
  })
  const invalid = parsed.findIndex((p) => !MAC_RE.test(p.mac))
  const runner = useBatchRunner(
    async (idx, force) =>
      batch.mutateAsync({
        idOrCode,
        reservations: idx.flatMap((i, n) => {
          const r = parsed[i]
          return r ? [n === 0 && force ? { ...r, force: true } : r] : []
        }),
      }),
    parsed.length,
    onClose,
  )
  const conflicted = runner.conflictAt !== null

  return (
    <>
      <Dialog
        open={!conflicted}
        title="Add reservations in bulk"
        onClose={onClose}
        footer={
          <>
            <Button variant="quiet" onClick={onClose} disabled={runner.pending}>
              Cancel
            </Button>
            <Button
              onClick={() => runner.go(0, false)}
              disabled={parsed.length === 0 || invalid >= 0 || runner.pending}
            >
              {runner.pending ? 'Adding...' : `Add ${parsed.length || ''} reservations`.trim()}
            </Button>
          </>
        }
      >
        <Field label="One per line: MAC, type, VLAN" hint="Type and VLAN are optional (default workstation, 100).">
          <textarea
            className={`${inputCls} h-40 py-2`}
            style={mono}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="aa:bb:cc:dd:ee:01, workstation, 100"
          />
        </Field>
        {invalid >= 0 ? <Inline tone="bad">Line {invalid + 1} doesn't have a valid MAC address.</Inline> : null}
        {runner.error ? <Inline tone="bad">{runner.error}</Inline> : null}
        {runner.done > 0 && !runner.error ? <Inline tone="muted">{runner.done} added so far.</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={conflicted}
        title={`Line ${(runner.conflictAt ?? 0) + 1} conflicts`}
        body={`${FORCE_BODY} ${runner.done} reservation${runner.done === 1 ? ' was' : 's were'} added before the conflict.`}
        confirmLabel="Force and continue"
        danger
        pending={runner.pending}
        onConfirm={() => runner.go(runner.conflictAt ?? 0, true)}
        onClose={runner.clear}
      />
    </>
  )
}

function EditDialog({
  idOrCode,
  code,
  res,
  onClose,
}: {
  idOrCode: string
  code: string
  res: SelectReservation
  onClose: () => void
}) {
  const edit = useEditReservationsBatch()
  const [mac, setMac] = useState(res.mac ?? '')
  const [type, setType] = useState(res.type ?? 'workstation')
  const [conflict, setConflict] = useState(false)
  const valid = MAC_RE.test(mac.trim()) && type.trim() !== ''

  const submit = (force: boolean) =>
    edit.mutate(
      {
        idOrCode,
        changes: [
          { oldMac: res.mac ?? '', next: editedReservationInput(code, mac.trim().toLowerCase(), type.trim(), force) },
        ],
      },
      {
        onSuccess: (r) => (r.conflict ? setConflict(true) : onClose()),
      },
    )

  return (
    <>
      <Dialog
        open={!conflict}
        title="Edit reservation"
        onClose={onClose}
        footer={
          <>
            <Button variant="quiet" onClick={onClose} disabled={edit.isPending}>
              Cancel
            </Button>
            <Button onClick={() => submit(false)} disabled={!valid || edit.isPending}>
              {edit.isPending ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-[var(--color-muted)]">
          Editing replaces the reservation: the old entry is removed and a new one is created on VLAN 100.
        </p>
        <Field label="MAC address">
          <input className={inputCls} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
        </Field>
        <Field label="Type">
          <TypeInput value={type} onChange={setType} />
        </Field>
        {edit.error ? <Inline tone="bad">{apiErrorMessage(edit.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={conflict}
        title="This reservation conflicts"
        body={FORCE_BODY}
        confirmLabel="Force save"
        danger
        pending={edit.isPending}
        onConfirm={() => {
          setConflict(false)
          submit(true)
        }}
        onClose={() => setConflict(false)}
      />
    </>
  )
}

export function PorcelainReservations() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const reservations = useReservations(idOrCode)
  const del = useDeleteReservation()
  const refresh = useRefreshReservations()

  const [addOpen, setAddOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [editRes, setEditRes] = useState<SelectReservation | null>(null)
  const [deleteRes, setDeleteRes] = useState<SelectReservation | null>(null)

  const header = (
    <PageHeader
      eyebrow={<SubCrumb idOrCode={idOrCode} section="network" page="Reservations" />}
      title="Reservations"
      subtitle="Fixed DHCP addresses for devices in this store."
    />
  )

  if (location.isLoading || reservations.isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={2} />
      </section>
    )
  const loc = location.data
  if (location.error || reservations.error || !loc)
    return (
      <section>
        {header}
        <Notice
          tone="error"
          title="We couldn't load reservations"
          body="Check your connection and try again in a moment."
        />
      </section>
    )

  const code = loc.aptosStoreCode ?? loc.code ?? idOrCode
  const list = reservations.data ?? []

  return (
    <section>
      {header}
      <div className="mt-8">
        <Card
          title={`${list.length} reservation${list.length === 1 ? '' : 's'}`}
          aside={
            <div className="flex items-center gap-1">
              <Button
                variant="quiet"
                disabled={refresh.isPending}
                onClick={() => refresh.mutate({ idOrCode, aptosCode: code })}
              >
                {refresh.isPending ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button variant="quiet" onClick={() => setBulkOpen(true)}>
                Add in bulk
              </Button>
              <Button onClick={() => setAddOpen(true)}>Add reservation</Button>
            </div>
          }
        >
          {refresh.error ? (
            <div className="px-5 pt-3">
              <Inline tone="bad">{apiErrorMessage(refresh.error)}</Inline>
            </div>
          ) : null}
          {list.length === 0 ? (
            <EmptyRow>No reservations yet. Add one to pin a device to a fixed address.</EmptyRow>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className={th}>Name</th>
                    <th className={th}>MAC</th>
                    <th className={th}>IP</th>
                    <th className={th}>Type</th>
                    <th className={th}>Status</th>
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                      <td className={`${td} font-medium`}>{r.name ?? '-'}</td>
                      <td className={td} style={mono}>
                        {r.mac}
                      </td>
                      <td className={td} style={mono}>
                        {r.ip ?? '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`}>{r.type ?? '-'}</td>
                      <td className={td}>
                        <Badge label={r.status ?? 'Unknown'} tone={statusTone(r.status)} />
                      </td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1">
                          <Button variant="quiet" onClick={() => setEditRes(r)}>
                            Edit
                          </Button>
                          <Button variant="danger" onClick={() => setDeleteRes(r)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {addOpen ? <AddDialog idOrCode={idOrCode} code={code} onClose={() => setAddOpen(false)} /> : null}
      {bulkOpen ? <BatchAddDialog idOrCode={idOrCode} code={code} onClose={() => setBulkOpen(false)} /> : null}
      {editRes ? <EditDialog idOrCode={idOrCode} code={code} res={editRes} onClose={() => setEditRes(null)} /> : null}
      <ConfirmDialog
        open={deleteRes !== null}
        title="Delete this reservation?"
        body={
          <>
            <span style={mono}>{deleteRes?.mac}</span> will lose its fixed address and may receive a different one.
          </>
        }
        confirmLabel="Delete"
        danger
        pending={del.isPending}
        error={del.error ? apiErrorMessage(del.error) : null}
        onConfirm={() =>
          deleteRes?.mac && del.mutate({ idOrCode, mac: deleteRes.mac }, { onSuccess: () => setDeleteRes(null) })
        }
        onClose={() => {
          del.reset()
          setDeleteRes(null)
        }}
      />
    </section>
  )
}
